<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import { RouterLink, RouterView, useRoute } from 'vue-router'

/**
 * The application shell: a skip link, a header, the routed page, a footer.
 *
 * Two things here exist for keyboard and screen-reader users and are easy to
 * leave out of a single-page application because nothing visibly breaks without
 * them.
 *
 * A route change in an SPA does not reload the document, so a screen reader is
 * never told the page changed. The live region below announces the new page
 * title after each navigation.
 *
 * Focus also survives a route change, which leaves a keyboard user tabbing from
 * wherever they were on the page they just left. Focus moves to the main
 * landmark instead, so the next Tab starts at the top of the new page. It is
 * skipped on first load, where nothing has moved and stealing focus would be
 * the rude option.
 */
const route = useRoute()
const announcement = ref('')
const main = ref<HTMLElement | null>(null)

/*
  No `immediate`, and no first-run guard.

  A guard was here to skip the initial page load, on the assumption that the
  watcher fires once on mount. It does not: without `immediate` the callback
  only runs on a change, so the first thing it sees is the first real
  navigation. The guard was swallowing exactly the case it was meant to let
  through, and the first route change in a session announced nothing and left
  focus wherever the click had put it.
*/
watch(
  () => route.fullPath,
  async () => {
    await nextTick()
    const title = typeof route.meta.title === 'string' ? route.meta.title : 'Page'
    announcement.value = `${title}. Loaded.`
    main.value?.focus()
  },
)
</script>

<template>
  <a
    href="#main"
    class="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:rounded-md focus:bg-surface focus:px-4 focus:py-3 focus:font-semibold focus:text-ink focus:shadow-lg focus:outline-2 focus:outline-focus"
  >
    Skip to content
  </a>

  <div class="flex min-h-dvh flex-col">
    <header class="sticky top-0 z-30 border-b border-line bg-canvas/85 backdrop-blur-sm">
      <div class="mx-auto flex w-full max-w-5xl items-center gap-6 px-4 py-3 sm:px-6">
        <RouterLink
          to="/"
          class="tap flex items-center gap-2.5 font-semibold tracking-tight text-ink"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
            class="text-accent"
          >
            <path d="M12 2.5 4 5.6v6.1c0 4.6 3.2 8.7 8 9.8 4.8-1.1 8-5.2 8-9.8V5.6Z" />
            <path d="m8.6 12 2.3 2.4 4.5-4.8" />
          </svg>
          healthcheck
        </RouterLink>

        <nav
          aria-label="Main"
          class="ml-auto flex items-center gap-1 text-sm"
        >
          <RouterLink
            to="/"
            class="tap flex items-center rounded-md px-3 text-ink-muted transition-colors duration-100 hover:text-ink aria-[current=page]:font-semibold aria-[current=page]:text-ink"
          >
            Scan a site
          </RouterLink>
          <RouterLink
            to="/checks"
            class="tap flex items-center rounded-md px-3 text-ink-muted transition-colors duration-100 hover:text-ink aria-[current=page]:font-semibold aria-[current=page]:text-ink"
          >
            What we test
          </RouterLink>
        </nav>
      </div>
    </header>

    <main
      id="main"
      ref="main"
      tabindex="-1"
      class="flex-1 focus:outline-none"
    >
      <RouterView />
    </main>

    <footer class="border-t border-line py-8 text-sm text-ink-faint">
      <div class="mx-auto flex w-full max-w-5xl flex-wrap items-center gap-x-6 gap-y-2 px-4 sm:px-6">
        <p>
          Reports are kept for a short time and then forgotten. A scan URL is the only thing that
          can read one, so treat it as private.
        </p>
      </div>
    </footer>
  </div>

  <div
    aria-live="polite"
    aria-atomic="true"
    class="sr-only"
  >
    {{ announcement }}
  </div>
</template>
